import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { usePageMeta } from "@/hooks/usePageMeta";

const features = [
  { icon: "ri-film-line", title: "影视资料聚合", desc: "汇聚电影、电视剧的海量资料信息，覆盖剧情、科幻、悬疑、爱情等多种题材。" },
  { icon: "ri-filter-3-line", title: "多维筛选浏览", desc: "支持按类型、地区、年份、题材等维度筛选，快速定位你想看的作品。" },
  { icon: "ri-search-eye-line", title: "快捷内容查找", desc: "通过片名、导演、主演或题材关键词搜索，轻松找到感兴趣的影视作品。" },
  { icon: "ri-heart-3-line", title: "本地收藏管理", desc: "喜欢的作品可一键收藏，收藏状态保存在本地，刷新后依然保留。" },
];

const stats = [
  { value: "30+", label: "收录影视作品" },
  { value: "4", label: "覆盖地区" },
  { value: "15", label: "细分题材" },
  { value: "每日", label: "持续更新" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 大师兄影视",
    description:
      "了解大师兄影视：一个专注影视资源聚合与展示的平台，为影迷提供最新电影、电视剧的资料查询、分类筛选与浏览发现服务。",
    keywords: "关于大师兄影视,大师兄影视介绍,影视资源平台,影视展示网站",
  });

  return (
    <SiteLayout>
      <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Stylized%20artistic%20wide%20shot%20of%20a%20modern%20cinema%20interior%20with%20rows%20of%20seats%20and%20a%20large%20glowing%20screen%20casting%20warm%20light%2C%20atmospheric%20moody%20lighting%2C%20deep%20blue%20and%20amber%20tones%2C%20clean%20minimal%20composition%2C%20premium%20cinematic%20feel%2C%20high%20detail&width=1600&height=600&seq=about-hero&orientation=landscape"
          alt="大师兄影视 观影空间"
          title="关于大师兄影视"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/60 to-foreground-950/30" />
        <div className="absolute inset-0 flex items-center">
          <div className="w-full px-4 md:px-8">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-semibold mb-3">
              <i className="ri-information-line" />
              About Us
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-background-50">关于大师兄影视</h1>
            <p className="mt-3 text-sm md:text-base text-background-100/90 max-w-2xl leading-7">
              一个专注影视资源聚合与展示的平台，陪你发现每一部值得看的好作品。
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="max-w-4xl">
          <span className="inline-flex items-center gap-1.5 text-accent-600 text-xs font-semibold uppercase tracking-wider mb-2">
            <span className="w-4 h-0.5 bg-accent-500 rounded-full" />
            Our Mission
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">网站定位</h2>
          <p className="mt-4 text-sm md:text-base text-foreground-700 leading-8">
            大师兄影视定位于影视内容的聚合与展示，面向喜欢观看、浏览多类型影视作品的普通影迷与影视爱好者。
            我们把分散的影视资料整理到一起，让用户可以快速浏览最新、热门的电影与电视剧，
            按类型、地区、年份等维度筛选，查看作品详情与相关资料，并发现相关的推荐作品。
          </p>
          <p className="mt-4 text-sm md:text-base text-foreground-700 leading-8">
            我们希望降低影视内容的发现成本，让每一次打开都是一次轻松的探索。平台聚焦于
            <strong className="text-foreground-950"> 内容展示与信息聚合 </strong>
            这一核心价值，不提供任何在线播放、下载或真实影视资源访问服务，
            所有作品的正片内容请前往正规影视平台观看，支持正版。
          </p>
        </div>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          {stats.map((s) => (
            <div key={s.label} className="rounded-lg bg-primary-500 px-4 py-5 md:px-6 md:py-6">
              <p className="font-heading text-2xl md:text-3xl font-extrabold text-background-50">{s.value}</p>
              <p className="mt-1 text-xs md:text-sm text-background-100/80">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full bg-background-100/60">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="mb-6">
            <span className="inline-flex items-center gap-1.5 text-accent-600 text-xs font-semibold uppercase tracking-wider mb-2">
              <span className="w-4 h-0.5 bg-accent-500 rounded-full" />
              Features
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">我们提供什么</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {features.map((f) => (
              <div key={f.title} className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                  <i className={`${f.icon} text-xl`} />
                </span>
                <h3 className="font-heading text-base font-bold text-foreground-950">{f.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-6">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-customer-service-2-line text-primary-600" />
              联系方式
            </h2>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                  <i className="ri-mail-line" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">反馈邮箱</p>
                  <p className="text-sm text-foreground-600">feedback@dsx-movie.com</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                  <i className="ri-time-line" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">处理时间</p>
                  <p className="text-sm text-foreground-600">工作日 09:00 - 18:00，我们会在 1-3 个工作日内回复</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                  <i className="ri-map-pin-line" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">办公地点</p>
                  <p className="text-sm text-foreground-600">北京市朝阳区 · 影视文创园区</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="rounded-lg bg-secondary-100 border border-secondary-200 p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-feedback-line text-accent-600" />
              意见反馈说明
            </h2>
            <p className="text-sm text-foreground-700 leading-7">
              如果你在使用过程中发现数据有误、希望补充某部影视作品的资料，或对网站功能有任何建议，
              欢迎通过上方邮箱与我们联系。请在邮件中尽量说明作品名称、问题描述或建议内容，
              以便我们更快地定位与处理。
            </p>
            <div className="mt-5 p-4 rounded-lg bg-background-50/70 border border-secondary-200">
              <p className="text-xs text-foreground-600 leading-6">
                <i className="ri-information-line mr-1 text-accent-600" />
                温馨提示：本站不提供影视播放与下载服务，相关请求恕无法受理。影视作品版权归各出品方所有。
              </p>
            </div>
            <Link
              to="/category/全部"
              className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-compass-3-line" />
              去浏览影视
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}